import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { ImageProcessingState, JobStatusType, SidePanelLayer } from "@nova/constants"
import { Layer } from "@nova/protobuf"
import { trimMiddle } from "@nova/util-strings"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import { updateVideoJob } from "features/pubsub/entityActions/jobs/video"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { get } from "nodash"
import { DeleteFlight } from "panels/content/side-panel/upload-flow/components/DeleteFlight"
import { UploadProgressBar } from "panels/content/side-panel/upload-flow/components/UploadProgressBar"
import { getSummaryFromUploads } from "panels/content/side-panel/upload-flow/module"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { useCallback, useMemo, useState } from "react"
import * as Icons from "@nova/component-icons"
import { openVideoFlight } from "plugins/Video/state/thunks"

export const VideoPreviewFilesDemo = () => {
    const { uploads, job, flightId, selectFlightById } = useUploadFlowContext()
    const dispatch = useAppDispatch()

    const flight = useAppSelector(selectFlightById(flightId)) as Layer
    const summary = getSummaryFromUploads(uploads)

    const [demoJobCreatedAt, setDemoJobCreatedAt] = useState<number>()
    const [demoUploadingProgress, setDemoUploadingProgress] = useState(0)

    const startMockedUploading = useCallback(async () => {
        setDemoJobCreatedAt(new Date().getTime())

        // Set job status to QUEUED.
        dispatch(
            updateVideoJob({
                id: flightId,
                updates: { status: JobStatusType.QUEUED },
                context: { flightId },
            })
        )

        // Simulate uploading for 5 seconds.
        await new Promise<void>((resolve) => {
            const interval = setInterval(() => {
                setDemoUploadingProgress((prev) => {
                    const progress = Math.min(1, prev + 0.01)
                    if (progress >= 1) {
                        clearInterval(interval)
                        resolve()
                    }
                    return progress
                })
            }, 50)
        })

        // Set job status to COMPLETED.
        dispatch(
            updateVideoJob({
                id: flightId,
                updates: { status: JobStatusType.COMPLETED },
                context: { flightId },
            })
        )
    }, [dispatch, flightId])

    const [videoFile, srtFile] = useMemo(() => {
        return [
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return !isSRT
            }),
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return isSRT
            }),
        ]
    }, [uploads])

    const videoName = get(videoFile, "name") as string
    const srtName = get(srtFile, "name") as string

    const hasBegunUploading = demoUploadingProgress > 0
    const isUploading = hasBegunUploading && demoUploadingProgress < 1
    const hasFinishedUploading = job?.status === JobStatusType.COMPLETED

    return (
        <>
            <div className="border-b border-gray-700" />

            {!hasFinishedUploading ? (
                <>
                    {/* File List (pre-upload stage) */}
                    {!hasBegunUploading && !hasFinishedUploading && (
                        <div className="my-2 flex flex-col gap-2">
                            <div className="flex justify-between">
                                <h5 className="text-gray-0">Ready to Upload</h5>
                                <h5 className="text-gray-100">{`${uploads.length} files`}</h5>
                            </div>
                            <div className="my-2 flex flex-col gap-1">
                                <FileItem name={videoName || "Video File Missing"} />
                                <FileItem name={srtName || "SRT File Missing"} />
                            </div>
                        </div>
                    )}

                    {/* Uploading Progress */}
                    {!!demoJobCreatedAt && (
                        <>
                            {/* Uploading Progress Bar */}
                            {isUploading && (
                                <UploadProgressBar
                                    progress={demoUploadingProgress}
                                    total={1}
                                    timeElapsed={demoJobCreatedAt}
                                />
                            )}
                        </>
                    )}
                </>
            ) : (
                <>
                    {/* "Upload Complete" Message */}
                    <div className="flex items-center gap-1 my-4 select-none">
                        <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                        <h5 className="text-gray-0">Upload Complete</h5>
                    </div>
                </>
            )}

            {/* Metadata */}
            <InspectorPanelMetadata
                entity={flight as SidePanelLayer}
                completedUploadCount={summary.stateCount[ImageProcessingState.UploadDone] || 0}
                hideCreator
                hideFileCount
                className="mt-2 border-b border-gray-700"
                isSingleFile
            />

            {/* Play Video Button */}
            {!hasBegunUploading ? (
                <ButtonTertiary
                    title="Play Video"
                    RightIcon={Icons.PlayCircle}
                    className="w-full mt-2"
                    onClick={() => dispatch(openVideoFlight({ flightId }))}
                />
            ) : (
                <ButtonPrimary
                    title="Play Video"
                    RightIcon={Icons.PlayCircle}
                    className="w-full mt-2"
                    onClick={() => dispatch(openVideoFlight({ flightId }))}
                />
            )}

            {/* Delete & Upload Buttons */}
            <div className="flex gap-2 items-center mt-2">
                <DeleteFlight />

                {/* Upload Button */}
                {!hasBegunUploading && (
                    <ButtonPrimary
                        title="Upload"
                        onClick={startMockedUploading}
                        className="w-full"
                        disabled={hasBegunUploading}
                    />
                )}
            </div>
        </>
    )
}

const FileItem = ({ name }: { name: string }) => {
    return (
        <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
                <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                <small className="text-gray-100 normalLineHeight">
                    {trimMiddle(name, { maxLength: 25 })}
                </small>
            </div>
        </div>
    )
}
